import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import messageService from '../../services/messageService';
import { useMessages } from '../../contexts/MessageContext';

const ACCENT = '#ffcc00';
const SIDEBAR_BG = 'rgb(28, 37, 54)';

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },

  /* header */
  convHeader: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
    borderBottom: '1px solid #e5e7eb', background: '#fff', flexShrink: 0,
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
    padding: '4px 6px', borderRadius: 5, fontSize: 15,
    '&:hover': { background: '#f3f4f6', color: '#111827' },
  },
  contactAvatar: {
    width: 36, height: 36, borderRadius: '50%', background: SIDEBAR_BG,
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 15, flexShrink: 0,
  },
  contactName: { fontWeight: 600, fontSize: 15, color: '#111827', flex: 1 },

  /* messages area */
  msgArea: {
    flex: 1, overflowY: 'auto', padding: '16px 20px',
    background: '#f3f4f6', display: 'flex', flexDirection: 'column', gap: 10,
  },
  empty: { display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', flex: 1 },

  bubble: {
    maxWidth: '72%', padding: '10px 14px', borderRadius: 12, wordBreak: 'break-word',
    fontSize: 14, lineHeight: 1.5,
    '@media (max-width: 600px)': { maxWidth: '90%' },
  },
  myBubble: {
    background: SIDEBAR_BG, color: '#fff', alignSelf: 'flex-end',
    borderBottomRightRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  theirBubble: {
    background: '#fff', color: '#111827', alignSelf: 'flex-start',
    borderBottomLeftRadius: 4, border: '1px solid #e5e7eb',
  },
  bubbleMeta: { fontSize: 11, opacity: 0.6, marginTop: 5, textAlign: 'right' },
  theirMeta: { textAlign: 'left', color: '#6b7280', opacity: 1 },
  urgentBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#fee2e2', color: '#b91c1c', fontSize: 11, fontWeight: 600,
    padding: '2px 7px', borderRadius: 4, marginBottom: 5,
  },
  typeBadge: {
    display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
    marginLeft: 6, verticalAlign: 'middle',
  },
  typeBadgeInfo:     { background: '#dbeafe', color: '#1d4ed8' },
  typeBadgeFeedback: { background: '#dcfce7', color: '#15803d' },
  typeBadgeError:    { background: '#fee2e2', color: '#b91c1c' },

  /* reply box */
  replyBox: {
    background: '#fff', borderTop: '1px solid #e5e7eb',
    padding: '10px 16px 14px', flexShrink: 0,
  },
  replyOptions: { display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' },
  toggleBtn: {
    padding: '4px 12px', borderRadius: 5, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 500, color: '#374151',
    '&:hover': { background: '#f3f4f6' },
  },
  toggleBtnActive: {
    background: 'rgb(28,37,54) !important', color: '#fff !important', borderColor: 'rgb(28,37,54) !important',
  },
  replyRow: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  replyTextarea: {
    flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit',
    minHeight: 44, maxHeight: 160, overflowY: 'auto', transition: 'border-color 0.15s',
    '&:focus': { borderColor: ACCENT },
  },
  sendBtn: {
    width: 40, height: 40, borderRadius: 8, border: 'none', flexShrink: 0,
    background: SIDEBAR_BG, color: '#fff', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 15,
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
    '&:not(:disabled):hover': { opacity: 0.85 },
  },
  hint: { fontSize: 11, color: '#9ca3af', marginTop: 5, textAlign: 'right' },
}));

const TYPE_TAGS = {
  INFO:     { label: 'Info',     cls: 'typeBadgeInfo' },
  FEEDBACK: { label: 'Feedback', cls: 'typeBadgeFeedback' },
  ERROR:    { label: 'Error',    cls: 'typeBadgeError' },
};

const MESSAGE_TYPES = ['INFO','FEEDBACK','ERROR'];
const PRIORITIES    = ['normal','urgent'];

export default function MessagesConversation({ userId }) {
  const classes = useStyles();
  const history = useHistory();
  const { otherUserId } = useParams();
  const otherId = Number(otherUserId);
  const endRef = useRef(null);
  const { decrementUnread, newMessageTimestamp, notifyMarkedRead } = useMessages();

  const [messages, setMessages]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [sending, setSending]       = useState(false);
  const [contactName, setContactName] = useState(`User ${otherId}`);
  const [content, setContent]       = useState('');
  const [msgType, setMsgType]       = useState('INFO');
  const [priority, setPriority]     = useState('normal');

  const scroll = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });

  const markAllRead = useCallback((msgs) => {
    const unread = msgs.filter(m => !m.isRead);
    if (!unread.length) return;
    decrementUnread(unread.length);
    Promise.all(unread.map(m => messageService.markAsRead(m.id).catch(() => {})))
      .then(() => notifyMarkedRead());
  }, [decrementUnread, notifyMarkedRead]);

  // Auto-reload and mark read when a new message arrives via SignalR
  useEffect(() => { if (newMessageTimestamp) load(); }, [newMessageTimestamp]); // eslint-disable-line

  const load = useCallback(() => {
    if (!userId || !otherId) return;
    setLoading(true);
    messageService.getConversation(userId, otherId)
      .then(r => {
        const msgs = [...(r.data || [])].reverse();
        setMessages(msgs);
        markAllRead(msgs);
        const cleanName = (n) => n?.replace(/\s*-\s*Split$/i, '') || '';
        const their = msgs.find(m => m.senderId === otherId);
        if (their?.senderName) setContactName(cleanName(their.senderName));
        else {
          const sent = msgs.find(m => m.recipientId === otherId);
          if (sent?.recipientName) setContactName(cleanName(sent.recipientName));
        }
        setLoading(false);
      })
      .catch(() => { toast.error('Failed to load conversation'); setLoading(false); });
  }, [userId, otherId, markAllRead]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { scroll(); }, [messages]);

  const fmtTime = (d) => {
    if (!d) return '';
    const dt = new Date(d), now = new Date();
    const isToday = dt.toDateString() === now.toDateString();
    const time = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return isToday ? time : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + time;
  };

  const sendReply = async () => {
    if (!content.trim() || sending) return;
    setSending(true);
    const last = messages[messages.length - 1];
    const subject = last ? `Re: ${last.subject.replace(/^Re: /, '')}` : 'New Message';
    try {
      await messageService.sendMessage({ senderId: 6948, recipientId: otherId, subject, content: content.trim(), messageType: msgType, priority });
      setContent('');
      load();
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const onKeyDown = (e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendReply(); };

  const initial = contactName.charAt(0).toUpperCase();

  return (
    <div className={classes.root}>
      {/* header */}
      <div className={classes.convHeader}>
        <button className={classes.backBtn} onClick={() => history.push('/messages/inbox')}>
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div className={classes.contactAvatar}>{initial}</div>
        <span className={classes.contactName}>{contactName}</span>
      </div>

      {/* messages */}
      <div className={classes.msgArea}>
        {loading
          ? <div className={classes.empty}><CircularProgress size={28} /></div>
          : messages.length === 0
          ? <div className={classes.empty}>No messages yet</div>
          : messages.map(msg => {
            const mine = msg.senderId === userId;
            const tag = TYPE_TAGS[msg.messageType?.typeCode];
            return (
              <div key={msg.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '72%' }}>
                {!mine && msg.senderName && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, paddingLeft: 2 }}>
                    {msg.senderName.replace(/\s*-\s*Split$/i, '')}
                  </div>
                )}
                {msg.priority === 'urgent' && (
                  <div className={classes.urgentBadge}>
                    <i className="fa-solid fa-circle-exclamation" /> Urgent
                  </div>
                )}
                <div className={`${classes.bubble} ${mine ? classes.myBubble : classes.theirBubble}`}>
                  {msg.content}
                  <div className={`${classes.bubbleMeta} ${!mine ? classes.theirMeta : ''}`}>
                    {fmtTime(msg.createdAt)}
                    {tag && <span className={`${classes.typeBadge} ${classes[tag.cls]}`}>{tag.label}</span>}
                  </div>
                </div>
              </div>
            );
          })
        }
        <div ref={endRef} />
      </div>

      {/* reply box */}
      <div className={classes.replyBox}>
        <div className={classes.replyOptions}>
          {MESSAGE_TYPES.map(t => (
            <button key={t}
              className={`${classes.toggleBtn} ${msgType === t ? classes.toggleBtnActive : ''}`}
              onClick={() => setMsgType(t)}>
              {t.charAt(0) + t.slice(1).toLowerCase()}
            </button>
          ))}
          <span style={{ borderLeft: '1px solid #e5e7eb', margin: '0 2px' }} />
          {PRIORITIES.map(p => (
            <button key={p}
              className={`${classes.toggleBtn} ${priority === p ? classes.toggleBtnActive : ''}`}
              onClick={() => setPriority(p)}>
              {p === 'urgent' ? '⚠ Urgent' : 'Normal'}
            </button>
          ))}
        </div>

        <div className={classes.replyRow}>
          <textarea
            className={classes.replyTextarea}
            placeholder="Write a reply…"
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={5000}
            rows={2}
          />
          <button className={classes.sendBtn} onClick={sendReply} disabled={sending || !content.trim()}>
            {sending
              ? <CircularProgress size={16} style={{ color: '#fff' }} />
              : <i className="fa-solid fa-paper-plane" />
            }
          </button>
        </div>
        <div className={classes.hint}>Ctrl + Enter to send</div>
      </div>
    </div>
  );
}
